/**
 * Drawer Drag Handlers for Native Interactive Site Map (ISM)
 *
 * Pure DOM event delegation — no Redux, no CSS, no business logic. Reports a drag gesture on the
 * shared layout drawer (`[data-drawer]`), firing once per gesture. The drawer's own
 * gesture/threshold logic (layout/components/drawer.js) is deliberately NOT reused here — this
 * module only observes, mirroring handlers/chipScrollHandlers.js. A pointerdown that moves past
 * MIN_DRAG_PX before pointerup counts as a drag; a plain tap (no meaningful movement) does not, so
 * tap-to-expand never reports as a drag. Mobile-only gating is the caller's responsibility (see
 * plugins/tracking.plugin.js) — this module doesn't know about breakpoints.
 *
 * @module nativeInteractiveSiteMap/handlers/drawerDragHandlers
 */
import { fromEvent, merge } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

// Movement (px, on the Y axis) a pointer must travel while held down before the gesture counts as
// a drag rather than a tap. Small but non-zero to absorb finger jitter on a tap.
const MIN_DRAG_PX = 10;

function createDrawerDragHandlers() {
  const state = {
    subscription: null,
  };

  /**
   * @param {HTMLElement} element - the drawer element to observe (e.g. `[data-drawer]`)
   * @param {() => void} onDragged - invoked once per drag gesture
   */
  function bind(element, onDragged) {
    const pointerDown$ = fromEvent(element, 'pointerdown');
    // Move/up are tracked on the document, not the element: a drag can travel outside the drawer
    // bounds before the pointer is released, exactly as layout/components/drawer.js listens on the
    // document for the same reason.
    const pointerMove$ = fromEvent(document, 'pointermove');
    const pointerUp$ = merge(fromEvent(document, 'pointerup'), fromEvent(document, 'pointercancel'));

    state.subscription = pointerDown$
      .pipe(
        switchMap((down) => {
          // Scope move/up to the pointer that started the gesture, so a second finger (multi-touch)
          // or a stray pen/trackpad pointer can't count as a drag or end the gesture early.
          const isSamePointer = (event) => event.pointerId === down.pointerId;
          return pointerMove$.pipe(
            filter(isSamePointer),
            filter((move) => Math.abs(move.clientY - down.clientY) >= MIN_DRAG_PX),
            // One emission per gesture: the first qualifying move ends this inner stream, and
            // takeUntil(same-pointer up) discards gestures that never move far enough (taps).
            take(1),
            takeUntil(pointerUp$.pipe(filter(isSamePointer))),
            map(() => undefined)
          );
        })
      )
      .subscribe(onDragged);
  }

  function unbind() {
    state.subscription?.unsubscribe();
    state.subscription = null;
  }

  return { bind, unbind };
}

export default createDrawerDragHandlers;
